// ResetPassword.jsx — Définition d'un nouveau mot de passe (redesign éditorial)
import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Lock, Loader2, CheckCircle2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Logo } from '@/components/Logo';

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error('Les mots de passe ne correspondent pas');
    if (password.length < 6) return toast.error('Mot de passe trop court (min 6 caractères)');
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, mot_de_passe: password });
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Lien invalide ou expiré');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8] text-[#0F172A] font-sans antialiased grid place-items-center p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">

        <Link to="/" className="inline-flex items-center gap-2.5 mb-10 group">
          <Logo size={30} className="transition-transform group-hover:scale-105" />
          <span className="font-display font-black tracking-tight text-[#0F172A]">AssoPerf</span>
        </Link>

        {done ? (
          <div className="bg-white border border-[#E2E8F0] rounded-3xl p-9 text-center" data-testid="reset-success">
            <div className="w-14 h-14 rounded-2xl bg-[#059669]/10 grid place-items-center mx-auto mb-6">
              <CheckCircle2 className="w-7 h-7 text-[#059669]" />
            </div>
            <h1 className="font-display text-3xl font-bold text-[#0F172A] mb-3 leading-tight">
              Mot de passe <span className="italic font-medium text-[#0047FF]">modifié.</span>
            </h1>
            <p className="text-sm text-[#475569] leading-relaxed mb-7">
              Redirection vers la connexion dans quelques secondes…
            </p>
            <Link
              to="/login"
              className="group inline-flex items-center justify-center gap-2 w-full px-6 py-3.5 rounded-xl bg-[#0047FF] hover:bg-[#0040E0] text-white font-bold text-base transition-all shadow-md hover:shadow-lg"
              data-testid="reset-login-link"
            >
              Se connecter
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0047FF] mb-4">Sécurité</div>
              <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0F172A] leading-[1.02] mb-3">
                Nouveau <span className="italic font-medium text-[#0047FF]">mot de passe.</span>
              </h1>
              <p className="text-sm text-[#475569] leading-relaxed">
                Choisissez un mot de passe sécurisé d'au moins 6 caractères.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">Nouveau mot de passe</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="ap-input"
                  placeholder="••••••••"
                  data-testid="reset-password-input"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">Confirmer</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="ap-input"
                  placeholder="••••••••"
                  data-testid="reset-confirm-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#0047FF] hover:bg-[#0040E0] text-white font-bold text-base transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                data-testid="reset-submit-button"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4" /> Modifier mon mot de passe</>}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
